import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import AWNLogo from '../components/AWNLogo';
import {
  getSession,
  logoutUser,
  normalizeEmail,
  onAuthStateChange,
  updatePassword,
  verifyEmailToken,
} from '../services/authService';
import { getPasswordStrength, validateNewPassword } from '../utils/authValidation';
import styles from '../style/ForgotPasswordPage.module.css';

function getLinkError() {
  if (typeof window === 'undefined') return '';
  const query = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  return query.get('error_description') || hash.get('error_description') || '';
}

function getRecoveryToken() {
  if (typeof window === 'undefined') return null;
  const query = new URLSearchParams(window.location.search);
  const tokenHash = query.get('token_hash');
  if (!tokenHash) return null;
  return { tokenHash, type: query.get('type') || 'recovery' };
}

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [linkState, setLinkState] = useState('checking');
  const [accountEmail, setAccountEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const passwordStrength = getPasswordStrength(password);
  const strengthLabels = ['Sehr schwach', 'Schwach', 'Mittel', 'Gut', 'Stark', 'Sehr stark'];

  useEffect(() => {
    let active = true;
    let invalidTimer;
    const recoveryToken = getRecoveryToken();
    let tokenValidated = !recoveryToken;

    const acceptSession = (session) => {
      if (!active || !tokenValidated || !session?.user) return false;
      setAccountEmail(normalizeEmail(session.user.email));
      setLinkState('ready');
      if (invalidTimer) window.clearTimeout(invalidTimer);
      return true;
    };

    const linkError = getLinkError();
    if (linkError) {
      setStatus({ type: 'error', message: 'Dieser Reset-Link ist ungültig oder abgelaufen.' });
      setLinkState('invalid');
      return undefined;
    }

    const authListener = onAuthStateChange((_event, session) => acceptSession(session));

    const loadRecoverySession = async () => {
      if (recoveryToken) {
        await verifyEmailToken(recoveryToken.tokenHash, recoveryToken.type);
        tokenValidated = true;
      }
      return getSession();
    };

    loadRecoverySession()
      .then((session) => {
        if (!acceptSession(session) && active) {
          invalidTimer = window.setTimeout(() => {
            if (active) {
              setStatus({ type: 'error', message: 'Dieser Reset-Link ist ungültig oder abgelaufen.' });
              setLinkState('invalid');
            }
          }, 1200);
        }
      })
      .catch(() => {
        if (active) {
          setStatus({ type: 'error', message: 'Der Reset-Link konnte nicht geprüft werden.' });
          setLinkState('invalid');
        }
      });

    return () => {
      active = false;
      if (invalidTimer) window.clearTimeout(invalidTimer);
      authListener?.data?.subscription?.unsubscribe();
    };
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = validateNewPassword(password, confirmPassword);
    setErrors(nextErrors);
    setStatus(null);
    if (Object.keys(nextErrors).length > 0) return;

    setIsLoading(true);
    try {
      await updatePassword(password);
      try {
        await logoutUser();
      } catch (_signOutError) {
        // The password was changed successfully; a failed local sign-out must not
        // make the user repeat the password update.
      }
      setPassword('');
      setConfirmPassword('');
      setLinkState('success');
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.pageContainer}>
      <div className={styles.backgroundPattern} aria-hidden="true" />
      <div className={styles.splitContainer}>
        <aside className={styles.socialColumn}>
          <div className={styles.socialLogoSection}>
            <AWNLogo width={300} height={100} className={styles.companyLogo} />
          </div>
          <p className={styles.sideCopy}>Wählen Sie ein neues Passwort, das Sie nicht für andere Dienste verwenden.</p>
        </aside>

        <section className={styles.forgotCard}>
          {linkState === 'checking' && (
            <div className={styles.outcomePanel} role="status">
              <div className={styles.outcomeIcon} aria-hidden="true">…</div>
              <h1 className={styles.forgotTitle}>Reset-Link wird geprüft</h1>
              <p className={styles.forgotSubtitle}>Einen Moment bitte.</p>
            </div>
          )}

          {linkState === 'invalid' && (
            <div className={styles.outcomePanel}>
              <p className={styles.eyebrow}>Link nicht verfügbar</p>
              <h1 className={styles.forgotTitle}>Neuen Reset-Link anfordern</h1>
              {status && <div className={styles.errorText} role="alert">{status.message}</div>}
              <p className={styles.forgotSubtitle}>Reset-Links können ablaufen oder nur einmal verwendet werden.</p>
              <Link to="/forgot-password" className={styles.primaryLink}>Neuen Link anfordern</Link>
              <Link to="/login" className={styles.textLink}>Zur Anmeldung</Link>
            </div>
          )}

          {linkState === 'ready' && (
            <>
              <div className={styles.logoSection}>
                <p className={styles.eyebrow}>Für {accountEmail || 'Ihr Konto'}</p>
                <h1 className={styles.forgotTitle}>Neues Passwort festlegen</h1>
                <p className={styles.forgotSubtitle}>Mindestens 8 Zeichen; 12 oder mehr sind besser.</p>
              </div>

              <form onSubmit={handleSubmit} className={styles.forgotForm} noValidate>
                <div className={styles.inputGroup}>
                  <label htmlFor="new-password">Neues Passwort</label>
                  <div className={styles.inputWrapper}>
                    <input type={showPassword ? 'text' : 'password'} id="new-password" value={password} onChange={(event) => { setPassword(event.target.value); setErrors((current) => ({ ...current, password: '' })); }} autoComplete="new-password" aria-invalid={Boolean(errors.password)} disabled={isLoading} />
                    <span className={styles.inputIcon} aria-hidden="true">●</span>
                    <button type="button" className={styles.passwordToggle} onClick={() => setShowPassword((visible) => !visible)} aria-label={showPassword ? 'Passwort ausblenden' : 'Passwort anzeigen'}>
                      {showPassword ? <VisibilityOff fontSize="inherit" /> : <Visibility fontSize="inherit" />}
                    </button>
                  </div>
                  {password && <div className={styles.passwordMeter}>Passwortstärke: <strong>{strengthLabels[passwordStrength]}</strong></div>}
                  {errors.password && <span className={styles.fieldError}>{errors.password}</span>}
                </div>

                <div className={styles.inputGroup}>
                  <label htmlFor="confirm-password">Neues Passwort wiederholen</label>
                  <div className={styles.inputWrapper}>
                    <input type={showConfirmPassword ? 'text' : 'password'} id="confirm-password" value={confirmPassword} onChange={(event) => { setConfirmPassword(event.target.value); setErrors((current) => ({ ...current, confirmPassword: '' })); }} autoComplete="new-password" aria-invalid={Boolean(errors.confirmPassword)} disabled={isLoading} />
                    <span className={styles.inputIcon} aria-hidden="true">●</span>
                    <button type="button" className={styles.passwordToggle} onClick={() => setShowConfirmPassword((visible) => !visible)} aria-label={showConfirmPassword ? 'Passwort ausblenden' : 'Passwort anzeigen'}>
                      {showConfirmPassword ? <VisibilityOff fontSize="inherit" /> : <Visibility fontSize="inherit" />}
                    </button>
                  </div>
                  {errors.confirmPassword && <span className={styles.fieldError}>{errors.confirmPassword}</span>}
                </div>

                {status && <div className={styles.errorText} role="alert">{status.message}</div>}
                <button type="submit" className={styles.submitButton} disabled={isLoading}>
                  {isLoading ? 'Passwort wird gespeichert…' : 'Neues Passwort speichern'}
                </button>
              </form>
            </>
          )}

          {linkState === 'success' && (
            <div className={styles.outcomePanel} aria-live="polite">
              <div className={styles.outcomeIcon} aria-hidden="true">✓</div>
              <p className={styles.eyebrow}>Erfolgreich geändert</p>
              <h1 className={styles.forgotTitle}>Ihr Passwort ist bereit</h1>
              <p className={styles.forgotSubtitle}>Melden Sie sich jetzt mit Ihrem neuen Passwort an.</p>
              <button type="button" className={styles.primaryButton} onClick={() => navigate('/login', { replace: true, state: { email: accountEmail, notice: 'password_reset' } })}>Jetzt anmelden</button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
